import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Dayjs } from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { generatePath, useNavigate } from "react-router-dom";
import SearchableContent from "../../Layout/SearchableContent";
import {
  PaginationPartyBalance,
  PartyBalance,
  useBalanceSheetLazyQuery,
} from "../../generated/graphql";
import useDebounce from "../../hooks/useDebounce";
import useInputSearch from "../../hooks/useInputSearch";
import { useAppDispatch, useAppSelector } from "../../hooks/usereduxHooks";
import { ledgerActionSuccess } from "../../store/actionCreator/ledger.creator";
import SearchPlaces from "../../utils/SearchPlaces";
import { formattedNum, limitAllItem } from "../../utils/constant";
import PrintButton from "../printButton/PrintButton";

const BalanceSheetColumn = ["Party Code", "Party Name", "City"];

export const selectOptionsForParty = [
  {
    name: "Party Code",
    value: "partyCode",
  },
  {
    name: "Party Name",
    value: "partyName",
  },
  {
    name: "city",
    value: "city",
  },
];
export interface PropsType {
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
}

export function AccountBalanceDetails({ startDate, endDate }: PropsType) {
  const [page] = useState(0);
  const [limit] = useState(limitAllItem);
  const [search, setSearch] = useState("");
  const [searchKey, setSearchKey] = useState("partyName");
  const [crDr, setCrDrOption] = useState("");

  const dispatch = useAppDispatch();

  const [fetchBalanceSheetQuery, { loading }] = useBalanceSheetLazyQuery();

  const { ledgerBalance } = useAppSelector((store) => store.Ledger);

  const fetchItem = useCallback(
    async (searchInput?: string): Promise<void> => {
      try {
        const { data } = await fetchBalanceSheetQuery({
          variables: {
            findMatchPartyInput: {
              page: undefined,
              limit,
              partyName: search ? search.trim() : "",
              orderKey: searchInput ?? searchKey,
              startDate: startDate ? startDate.toISOString() : undefined,
              endDate: endDate ? endDate.toISOString() : undefined,
              crDr: crDr ? crDr : undefined,
            },
          },
          fetchPolicy: "no-cache",
        });

        dispatch(
          ledgerActionSuccess(data?.balanceSheet as PaginationPartyBalance),
        );
        if (searchInput) {
          toast.success("Sorting completed", { duration: 2000 });
        }
      } catch (err) {
        throw err;
      }
    },
    [crDr, dispatch, endDate, fetchBalanceSheetQuery, limit, search, searchKey, startDate],
  );

  useEffect(() => {
    if (!ledgerBalance?.items?.length) {
      fetchItem();
    }
  }, [fetchItem, ledgerBalance?.items?.length]);

  useDebounce(fetchItem, 500, [search, page, crDr, endDate, startDate]);

  const navigate = useNavigate();

  const filterSorting = (parameter: number) => {
    try {
      const searchPerameter = selectOptionsForParty[parameter];
      if (searchPerameter) {
        setSearchKey(searchPerameter.value);
        fetchItem(searchPerameter.value);
        toast.loading(`Sorting by ${searchPerameter.name}, Please wait...`, {
          duration: 2000,
        });
      }
    } catch (err) {}
  };

  useInputSearch({
    filterSorting,
  });

  const componentRef = useRef<React.ReactElement | null>();

  return (
    <SearchableContent className={searchKey}>
      <Box>
        <Box
          display="flex"
          alignItems={{ xs: "stretch", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              Party Ledger
            </Typography>
            <Typography variant="subtitle2">All parties ledgers</Typography>
          </Box>
          <PrintButton componentRef={componentRef} />
        </Box>

        <SearchPlaces
          selectOptions={selectOptionsForParty}
          setSearchKey={setSearchKey}
          searchKey={searchKey}
          search={search}
          setSearch={setSearch}
          selectOptionsType={[
            { name: "DR", value: "DR" },
            { name: "CR", value: "CR" },
          ]}
          hideSearch={false}
          crDr={crDr}
          onSelectCrDrOptions={setCrDrOption}
        />
        <Box ref={componentRef} sx={{ overflow: "auto", position: "relative" }}>
          <TableContainer>
            {loading && (
              <div className="loading-overlay">
                <CircularProgress />
              </div>
            )}
            <Table>
              <TableHead>
                <TableRow>
                  {BalanceSheetColumn.map((column) => {
                    return (
                      <TableCell className={column} key={column} align="left">
                        {column}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">Balance</TableCell>

                  <TableCell
                    align="right"
                    className="width_zero"
                    style={{ width: "0px" }}
                  ></TableCell>
                  <TableCell align="right">CR/DR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerBalance ? (
                  ledgerBalance.items?.map((item: PartyBalance) => (
                    <TableRow
                      onClick={() => {
                        const path = generatePath("/accounts/ledger/:partyId", {
                          partyId: String(item.id),
                        });
                        navigate(path);
                      }}
                      style={{ cursor: "pointer", textDecoration: "none" }}
                      key={item.id}
                    >
                      <TableCell className="partyCode Code">
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {item.partyCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="text.primary"
                          gutterBottom
                          noWrap
                          className="partyName"
                        >
                          {item.partyName}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography className="city">
                          {item?.city || "N/A"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography>
                          {formattedNum(Math.abs(item.balance!))}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        className="width_zero"
                        style={{ width: "0px" }}
                      ></TableCell>

                      <TableCell align="right">
                        <Typography
                          color={
                            item?.balance! > 0
                              ? "success.main"
                              : item?.balance! < 0
                              ? "error.main"
                              : "text.secondary"
                          }
                          fontWeight={600}
                        >
                          {item?.balance! > 0 ? "DR" : item?.balance! < 0 ? "CR" : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <></>
                )}
                {ledgerBalance?.totalBalance ? (
                  <TableRow
                    className="total_span"
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell
                      style={{ textTransform: "uppercase" }}
                      align="left"
                    ></TableCell>

                    <TableCell align="left">
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="text.primary"
                        gutterBottom
                        noWrap
                      >
                        Total
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="text.primary"
                        gutterBottom
                        noWrap
                      >
                        {ledgerBalance?.totalBalance
                          ? ledgerBalance?.totalBalance?.toFixed(2)
                          : 0}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{ textTransform: "uppercase" }}
                      align="left"
                    ></TableCell>
                    <TableCell
                      style={{ textTransform: "uppercase" }}
                      align="left"
                    ></TableCell>
                  </TableRow>
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* <Box p={2}>
        <TablePagination
          component="div"
          count={-1}
          onPageChange={(e, number) => {
            setPage(number);
          }}
          page={page}
          onRowsPerPageChange={(e) => setLimit(Number(e.target.value))}
          rowsPerPage={limit}
          rowsPerPageOptions={[15, 30, 50, 100]}
        />
      </Box> */}
      </Box>
    </SearchableContent>
  );
}
